import React, { useEffect, useState } from "react";
import "./sub1.css";
import { Helmet } from "react-helmet";
import { Link, useLocation } from "react-router-dom";
import { API_URL } from "../../confi";
import contactConfig from '../../confi';
// ✅ Convert blog title into SEO-friendly URL slug
const slugify = (title) =>
  title
    ?.toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");

const formatDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "2-digit",
  });
};

const BlogStandard = () => {
  const location = useLocation();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [blogsPerPage] = useState(6); // 6 blogs per page (2 rows of 3)

  // Predefined categories
  const predefinedCategories = [
    'all',
    'Housekeeping',
    'Baby Sitting',
    'Elderly Care',
    'Patient Care',
    'Cook',
    'Driver'
  ];

  const handleCall = () => {
    window.open(`tel:${contactConfig.phone}`);
  };

  const handleWhatsApp = () => {
    const phoneNumber = `${contactConfig.phone}`;
    const message = 'Hello, I would like to know more about your services.';
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/blogs`, {
        headers: { Accept: "application/json" },
      });
      console.log("fetch blog API response status:", res);
      const data = await res.json();
      console.log("Fetch Blogs Response:", JSON.stringify(data, null, 2));

      if (res.ok) {
        const blogsData = data.blogs?.data || data.blogs || [];

        // Transform API data - map author_name to predefined categories
        const transformedBlogs = blogsData.map(blog => {
          // Map author_name to predefined categories or use first predefined category as default
          const category = predefinedCategories.find(cat =>
            cat.toLowerCase() === (blog.author_name || '').toLowerCase()
          ) || predefinedCategories[1]; // Default to Housekeeping if no match

          return {
            id: blog.id,
            title: blog.title,
            content: blog.content || '',
            author: blog.author_name || 'Admin',
            date: blog.created_at || new Date().toISOString(),
            category: category,
            title_tag: blog.title_tag || blog.meta_title || '',
            meta_description: blog.meta_description || '',
            keywords: blog.keywords || blog.meta_keywords || '',
            url_title: blog.url_title || blog.slug || '',
            canonical_url: blog.canonical_url || '',
            og_title: blog.og_title || '',
            og_description: blog.og_description || '',
            meta_robots: blog.meta_robots || 'index, follow',
            image: blog.image?.startsWith("http")
              ? blog.image
              : blog.image
                ? `${API_URL}/${blog.image}`
                : `https://picsum.photos/400/300?random=${blog.id}`
          };
        });

        setBlogs(transformedBlogs);
        setError(null);
      } else {
        setError(data.message || "Failed to fetch blogs");
        setBlogs([]);
      }
    } catch (err) {
      console.error("Fetch blogs error:", err);
      setError("Failed to fetch blogs");
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  useEffect(() => {
    if (location.state?.category) {
      setSelectedCategory(location.state.category);
      setCurrentPage(1);
    }
    if (location.state?.search !== undefined) {
      setSearchTerm(location.state.search);
      setCurrentPage(1);
    }
  }, [location.state]);

  // Filter blogs based on category and search term
  const filteredBlogs = blogs.filter(blog => {
    const matchesCategory = selectedCategory === 'all' || blog.category === selectedCategory;
    const matchesSearch = blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (blog.content || '').toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Pagination logic
  const indexOfLastBlog = currentPage * blogsPerPage;
  const indexOfFirstBlog = indexOfLastBlog - blogsPerPage;
  const currentBlogs = filteredBlogs.slice(indexOfFirstBlog, indexOfLastBlog);
  const totalPages = Math.ceil(filteredBlogs.length / blogsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Get category display info with counts
  const getCategoryDisplayInfo = () => {
    const categoryCounts = {};
    blogs.forEach(blog => {
      categoryCounts[blog.category] = (categoryCounts[blog.category] || 0) + 1;
    });

    return predefinedCategories.filter(cat => cat !== 'all').map(category => ({
      name: category,
      count: categoryCounts[category] || 0
    }));
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      const startPage = Math.max(1, currentPage - 2);
      const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
    }

    return pageNumbers;
  };

  if (loading) {
    return (
      <div className="main-content-wrapper">
        <div className="blog-container">
          <div className="blog-loading">
            <div className="loading-spinner"></div>
            <p>Loading blogs...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && blogs.length === 0) {
    return (
      <div className="main-content-wrapper">
        <div className="blog-container">
          <div className="blog-error">
            <h2>Error Loading Blogs</h2>
            <p>{error}</p>
            <button onClick={() => fetchBlogs()} className="retry-btn">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content-wrapper">
      <Helmet>
        <title>Blog - Home Care and Staffing Advice | JP Home Services</title>
        <meta
          name="description"
          content="Read our blog for tips on hiring verified maids, and cooks, plus latest job vacancy updates for domestic staff in Gujarat. Call +918780438907 to learn more."
        />
        <meta
          name="keywords"
          content="house maid service, cooking tips, home cleaning, baby sitting, elderly care, housekeeping"
        />
        <link rel="canonical" href="https://jphomeservice.in/blog" />
      </Helmet>

      <div className="blog-container">
        {/* Hero Section */}
        <section className="blog-hero-section">
          <div className="blog-hero-content">
            <h1>Our Blog</h1>
            <p>Discover helpful tips, insights, and stories about housekeeping, cooking, home cleaning, and family care</p>
          </div>
        </section>
        <div className="floating-contact-icons">
          <div className="contact-icon whatsapp-icon" onClick={handleWhatsApp}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893c0-3.18-1.24-6.169-3.495-8.424" />
            </svg>
          </div>
          <div
            className="contact-icon call-icon"
            onClick={handleCall}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 10.999h2C22 5.869 18.127 2 12.99 2v2C17.052 4 20 6.943 20 10.999z" />
              <path d="M13 8c2.103 0 3 .897 3 3h2c0-3.225-1.775-5-5-5v2zm3.422 5.443a1.001 1.001 0 00-1.391.043l-2.393 2.461c-.576-.11-1.734-.471-2.926-1.66-1.192-1.193-1.553-2.354-1.66-2.926l2.459-2.394a1 1 0 00.043-1.391L6.859 3.513a1 1 0 00-1.391-.087l-2.17 1.861a1 1 0 00-.29.649c-.015.25-.301 6.172 4.291 10.766C11.305 20.707 16.323 21 17.705 21c.202 0 .326-.006.359-.008a.992.992 0 00.648-.291l1.86-2.171a1 1 0 00-.086-1.391l-4.064-3.696z" />
            </svg>
          </div>
        </div>

        {/* Search and Filter Section */}
        <section className="blog-filter-section">
          <div className="container">
            <div className="filter-content">
              <div className="search-box">
                <input
                  type="text"
                  placeholder="Search blogs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>

              <div className="category-filters">
                {predefinedCategories.map(category => (
                  <button
                    key={category}
                    className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedCategory(category);
                      setCurrentPage(1); // Reset to first page when category changes
                    }}
                  >
                    {category === 'all' ? 'All Posts' : category}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Blog Posts Grid */}
        <section className="blog-posts-section">
          <div className="container">
            {filteredBlogs.length === 0 ? (
              <div className="no-blogs">
                <h3>No blogs found</h3>
                <p>Try adjusting your search or filter criteria</p>
              </div>
            ) : (
              <>
                <div className="blogs-grid">
                  {currentBlogs
                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                    .map((blog) => {
                      const slug = blog.url_title || slugify(blog.title);
                      return (
                        <Link
                          key={blog.id}
                          to={`/blog/${slug}`}
                          state={{ blogId: blog.id, blog: blog }}
                          className="blog-card-link"
                        >
                          <article className="blog-card">
                            <div className="blog-image">
                              <img
                                src={blog.image}
                                alt={blog.title}
                                onError={(e) => {
                                  e.target.src = `https://picsum.photos/400/300?random=${blog.id}`;
                                }}
                              />
                              {/* Show category */}
                              <span className="blog-category">{blog.category}</span>
                            </div>

                            <div className="blog-content">
                              <h3 className="blog-title">{blog.title}</h3>

                              <div className="blog-meta">
                                <div className="meta-left">
                                  <span className="blog-date">{formatDate(blog.date)}</span>
                                </div>
                              </div>

                              <div className="read-more-btn">
                                Read More
                              </div>
                            </div>
                          </article>
                        </Link>
                      );
                    })}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="pagination">
                    <button
                      className={`pagination-btn ${currentPage === 1 ? 'disabled' : ''}`}
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>

                    <div className="page-numbers">
                      {getPageNumbers().map(number => (
                        <button
                          key={number}
                          className={`page-number ${currentPage === number ? 'active' : ''}`}
                          onClick={() => paginate(number)}
                        >
                          {number}
                        </button>
                      ))}
                    </div>

                    <button
                      className={`pagination-btn ${currentPage === totalPages ? 'disabled' : ''}`}
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* Newsletter Section */}
        <section className="blog-newsletter">
          <div className="container">
            <div className="newsletter-content">
              <h2>Stay Updated</h2>
              <p>Subscribe to our newsletter and never miss our latest blogs and updates</p>
              <div className="newsletter-form">
                <input
                  type="email"
                  placeholder="Enter your email address"
                  className="newsletter-input"
                />
                <button className="newsletter-btn">Subscribe</button>
              </div>
            </div>
          </div>
        </section>

        {/* Popular Categories */}
        <section className="popular-categories">
          <div className="container">
            <h2>Popular Categories</h2>
            <div className="categories-grid">
              {getCategoryDisplayInfo().map(categoryInfo => (
                <div key={categoryInfo.name} className="category-card">
                  <h3>{categoryInfo.name}</h3>
                  <p>{categoryInfo.count} articles</p>
                  <button
                    className="category-link"
                    onClick={() => {
                      setSelectedCategory(categoryInfo.name);
                      setCurrentPage(1);
                    }}
                  >
                    Explore
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default BlogStandard;